import type { PusherChannelConfig } from "@recruiter/shared";

import { ADMIN_API_BASE, EMPLOYEE_EMAIL } from "../config";

const env = import.meta.env;

/** Everything both configs share: the same Soketi/Pusher instance and the same
 *  employee identity. Only the auth ROUTE differs, and that difference is the
 *  point — see each config below. */
const base: Omit<PusherChannelConfig, "authEndpoint"> = {
  key: env.VITE_PUSHER_KEY ?? "local-key",
  host: env.VITE_PUSHER_HOST ?? "localhost",
  port: Number(env.VITE_PUSHER_PORT ?? "6201"),
  useTls: (env.VITE_PUSHER_USE_TLS ?? "false") === "true",
  cluster: env.VITE_PUSHER_CLUSTER ?? "mt1",
  authHeaders: EMPLOYEE_EMAIL ? { "x-employee-email": EMPLOYEE_EMAIL } : {},
};

/** Live Customer Support spectate config. The auth endpoint is area-gated and
 * verifies that the requested buyer channel belongs to a real project owner. */
export const adminPusherConfig: PusherChannelConfig = {
  ...base,
  authEndpoint: `${ADMIN_API_BASE}/api/admin/support/pusher/auth`,
  // A rep may park this tab while speaking with a customer. Keep the socket
  // hot; REST invalidation remains the authoritative reconcile path.
  idleDisconnectMs: 0,
};

/** Agent-debug chat config. A DIFFERENT auth route from the one above
 * (`/api/admin/pusher/auth`, in `web/routes/debug.py`), which signs the
 * employee's OWN `private-debug-<email>` channel; the support route would refuse
 * it. Do not merge the two — the routes authorize different channel families.
 *
 * The debug reply is a one-shot event with no REST reconcile behind it, so the
 * socket must survive a backgrounded tab: an operator asks a question and looks
 * away while a `gpt-5.4-mini` answer is composed. */
/** Auth for the channels an employee owns: `private-debug-<email>` (agent-debug
 *  chat, transcript translations) and `private-talent-<email>` (answered Talent
 *  selections). Signed by `POST /api/admin/pusher/auth` (`web/routes/debug.py`),
 *  which matches each by EQUALITY against the caller's own email.
 *
 *  NOT `adminPusherConfig`. That one points at
 *  `/api/admin/support/pusher/auth`, which signs `private-buyer-*` ONLY and
 *  requires the `customer_support` area — so an employee channel sent there 403s,
 *  the subscription never binds, and any flow waiting on it hangs with no error to
 *  show. It was called `debugPusherConfig` when the debug chat was its only user;
 *  the name is what made that mistake easy, so it says what it authenticates now. */
export const employeePusherConfig: PusherChannelConfig = {
  ...base,
  authEndpoint: `${ADMIN_API_BASE}/api/admin/pusher/auth`,
  idleDisconnectMs: 0,
};

/** The employee's private debug channel. Mirrors the backend's
 *  `recruiter.messaging.channel_for_debug`; a mismatch means the SPA silently
 *  subscribes to a channel nothing publishes to. */
export function debugChannelFor(email: string): string {
  return `private-debug-${email}`;
}

/** The employee's private Talent channel. Mirrors the backend's
 *  `recruiter.messaging.channel_for_talent`; a mismatch means the SPA silently
 *  subscribes to a channel nothing publishes to.
 *
 *  Derived from the identity the SHELL already loaded (`GET /api/admin/me`), never
 *  from a query response: the answer to a selection can land BEFORE a subscription
 *  created off that same response has bound, and with no timer anywhere in the read
 *  path a missed event is a permanent spinner. Subscribing from the identity closes
 *  that window because it does not depend on the request at all. */
export function talentChannelFor(email: string): string {
  return `private-talent-${email}`;
}
